import { INSTRUCTIONS_ROOT_NODE_NAME } from "@app/components/editor/extensions/instructions/InstructionsRootExtension";
import {
  findBlockByBlockId,
  parseHTMLToBlocks,
  replaceBlock,
} from "@app/lib/editor/instruction_blocks";
import {
  SKILL_TAG_NAME,
  UNAVAILABLE_SKILL_LABEL,
  UNAVAILABLE_SKILL_TAG_NAME,
} from "@app/lib/skills/format";
import { Extension } from "@tiptap/core";
import type { Node as PMNode, Schema, Slice } from "@tiptap/pm/model";
import {
  DOMSerializer,
  Fragment,
  DOMParser as PMDOMParser,
} from "@tiptap/pm/model";
import type { EditorState } from "@tiptap/pm/state";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Transform } from "@tiptap/pm/transform";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import { ChangeSet } from "prosemirror-changeset";

// A single block operation within a suggestion.
interface BlockOperation {
  targetBlockId: string;
  newContent: string; // HTML content.
}

// A stored suggestion with its operations.
interface StoredSuggestion {
  id: string;
  operations: BlockOperation[];
}

interface PluginState {
  suggestions: Map<string, StoredSuggestion>;
  highlightedId: string | null;
  decorations: DecorationSet;
}

// Change range from diffing old vs new content.
export interface BlockChange {
  fromA: number; // Range in old content.
  toA: number;
  fromB: number; // Range in new content.
  toB: number;
}

const pluginKey = new PluginKey<PluginState>("suggestionPlugin");

export const SUGGESTION_ID_ATTRIBUTE = "data-suggestion-id";

const CLASSES = {
  remove:
    "suggestion-deletion rounded line-through bg-warning-100 text-warning-800 cursor-default",
  removeDimmed:
    "suggestion-deletion rounded line-through bg-warning-50 text-muted-foreground cursor-default",
  add: "suggestion-addition rounded bg-highlight-100 text-highlight-800 cursor-default",
  addDimmed:
    "suggestion-addition rounded bg-highlight-50 text-muted-foreground cursor-default",
  blockHighlightDimmed: "suggestion-highlight rounded bg-muted cursor-default",
  hidden: "hidden",
  unchangedSeparator:
    "suggestion-separator my-2 h-px w-full shrink-0 bg-separator",
};

// Shared so plain-text field diffs look the same as instruction diffs.
export const SUGGESTION_DIFF_CLASSES = {
  add: CLASSES.add,
  remove: CLASSES.remove,
};

// Addition widgets are built by serializing the new nodes through `toDOM`
// (renderHTML), not through their React node views. <tool> and <knowledge> carry
// their own chip markup in renderHTML, so they show up in a diff for free;
// <skill> serializes as an EMPTY paired tag and would render as an invisible gap.
//
// SkillNode cannot take the same route: its renderHTML output also feeds the
// persisted instructionsHtml, and SKILL_REFERENCE_TAG_REGEX only matches a tag
// that closes immediately, so a chip child would make the rename and
// availability rewrites in `SkillResource.replaceSkillReferenceTags` silently
// skip stored references. (Tools avoid that because TOOL_ELEMENT_REGEX tolerates
// children.) So we label the reference here, where the markup is throwaway.
const SKILL_CHIP_CLASS =
  "inline-flex items-center gap-0.5 border border-current/40 rounded px-0.5 text-xs leading-tight";
const SKILL_LABEL_PREFIX = "Skill";

export function labelSkillReferences(container: HTMLElement): void {
  const references = container.querySelectorAll(
    `${SKILL_TAG_NAME}, ${UNAVAILABLE_SKILL_TAG_NAME}`
  );

  for (const reference of references) {
    // SkillNode's renderHTML emits an empty text child to force the paired tag
    // form, so emptiness is "renders nothing", not "has no child nodes".
    if (reference.textContent) {
      continue;
    }

    const isUnavailable =
      reference.tagName.toLowerCase() === UNAVAILABLE_SKILL_TAG_NAME;
    const name = reference.getAttribute("name");
    if (!isUnavailable && !name) {
      continue;
    }

    const chip = document.createElement("span");
    chip.className = SKILL_CHIP_CLASS;
    chip.textContent = isUnavailable
      ? UNAVAILABLE_SKILL_LABEL
      : `${SKILL_LABEL_PREFIX} ${name}`;
    reference.appendChild(chip);
  }
}

export function diffBlockContent(
  oldNode: PMNode,
  newNode: PMNode,
  schema: Schema
): BlockChange[] {
  // Empty old content means everything in newNode is an addition.
  if (oldNode.content.size === 0) {
    return [{ fromA: 0, toA: 0, fromB: 0, toB: newNode.content.size }];
  }

  // Cross-type replacement: new content isn't valid inside old node type, so we can't use
  // Transform.replaceWith for fine-grained diffing.
  // Return a single change covering everything as removed + added.
  if (oldNode.type !== newNode.type) {
    return [
      {
        fromA: 0,
        toA: oldNode.content.size,
        fromB: 0,
        toB: newNode.content.size,
      },
    ];
  }

  // If the schema has an `instructionsRoot` node and the target node isn't one,
  // we must wrap it: doc > instructionsRoot > block. This satisfies the schema
  // constraint that `doc` only accepts `instructionsRoot` children.
  const needsRoot =
    schema.nodes[INSTRUCTIONS_ROOT_NODE_NAME] !== undefined &&
    oldNode.type.name !== INSTRUCTIONS_ROOT_NODE_NAME;

  const blockNode = schema.node(
    oldNode.type.name,
    oldNode.attrs,
    oldNode.content
  );
  const docChildren = needsRoot
    ? [schema.node(INSTRUCTIONS_ROOT_NODE_NAME, null, [blockNode])]
    : [blockNode];
  const oldDoc = schema.node("doc", null, docChildren);

  // Extra nesting adds +1 to positions inside the content.
  const offset = needsRoot ? 2 : 1;

  const tr = new Transform(oldDoc);
  tr.replaceWith(offset, oldNode.content.size + offset, newNode.content);

  const changeSet = ChangeSet.create(oldDoc).addSteps(
    tr.doc,
    tr.mapping.maps,
    null
  );

  return changeSet.changes.map((change) => ({
    fromA: change.fromA - offset,
    toA: change.toA - offset,
    fromB: change.fromB - offset,
    toB: change.toB - offset,
  }));
}

// Returns the content to render in an addition widget for an inserted slice.
// Diff ranges include nested block boundaries, so a slice can wrap an
// inline-level change in its containing block(s). When the change stays within
// one text block, the slice is open on both ends and forms a single block chain
// — drill down to the inline content so it renders inline rather than as a
// phantom <p>/<li>. A genuine new block comes through closed (open depth 0) or
// as multiple children, so it keeps its structure. Each level peeled reduces the
// open depth by one (an open boundary always cuts through a non-leaf node, so
// firstChild is safe to descend into).
function inlineContentForInsertion(slice: Slice): Fragment {
  let { content } = slice;
  let { openStart, openEnd } = slice;

  while (
    openStart > 0 &&
    openEnd > 0 &&
    content.childCount === 1 &&
    !content.firstChild!.isInline
  ) {
    content = content.firstChild!.content;
    openStart -= 1;
    openEnd -= 1;
  }

  // Drop empty leading blocks so an inserted blank line or empty list item
  // doesn't render as extra blank widgets. `textContent` (not content.size)
  // catches empty wrappers like listItem > paragraph(), whose size is non-zero.
  // The childCount guard keeps the node when the whole insertion is one empty
  // block, so a single inserted blank line still shows one widget.
  while (content.childCount > 1 && content.firstChild!.textContent === "") {
    content = content.cut(content.firstChild!.nodeSize);
  }

  return content;
}

// Create inline diff decorations for a single block (deletion + addition widgets).
// An empty result means the block has no change.
function buildBlockDecorations({
  applyBlockHighlight,
  blockPos,
  isHighlighted,
  newNode,
  oldNode,
  schema,
  suggestionId,
}: {
  applyBlockHighlight: boolean;
  blockPos: number;
  isHighlighted: boolean;
  newNode: PMNode;
  oldNode: PMNode;
  schema: Schema;
  suggestionId: string;
}): Decoration[] {
  const decorations: Decoration[] = [];
  const changes = diffBlockContent(oldNode, newNode, schema);
  const contentStart = blockPos + 1;

  // Selected only: entire block gets dimmed highlight.
  if (applyBlockHighlight && changes.length > 0) {
    decorations.push(
      Decoration.node(blockPos, blockPos + oldNode.nodeSize, {
        class: CLASSES.blockHighlightDimmed,
        [SUGGESTION_ID_ATTRIBUTE]: suggestionId,
      })
    );
  }

  for (const change of changes) {
    if (change.fromA !== change.toA) {
      decorations.push(
        Decoration.inline(
          contentStart + change.fromA,
          contentStart + change.toA,
          {
            class: isHighlighted ? CLASSES.remove : CLASSES.removeDimmed,
            [SUGGESTION_ID_ATTRIBUTE]: suggestionId,
          }
        )
      );
    }

    if (change.fromB !== change.toB) {
      const isCrossType = oldNode.type !== newNode.type;
      // When old block is a different type, place the addition after it so the new content doesn't render inside the old block's container.
      const widgetPos = isCrossType
        ? blockPos + oldNode.nodeSize
        : contentStart + change.fromA;

      decorations.push(
        Decoration.widget(
          widgetPos,
          () => {
            const span = document.createElement("span");
            span.className = isHighlighted ? CLASSES.add : CLASSES.addDimmed;
            span.setAttribute(SUGGESTION_ID_ATTRIBUTE, suggestionId);
            span.contentEditable = "false";
            // ProseMirror sets `white-space: normal` on [contenteditable="false"]
            // elements, which collapses \n in code blocks. Override to inherit
            // the parent <pre>'s `white-space: pre-wrap`.
            span.style.whiteSpace = "inherit";

            const serializer = DOMSerializer.fromSchema(schema);
            if (isCrossType) {
              // Serialize the full block node so the preview shows the new type
              const blockEl = serializer.serializeNode(newNode, {});
              span.appendChild(blockEl);
            } else {
              const insertedContent = inlineContentForInsertion(
                newNode.slice(change.fromB, change.toB)
              );
              serializer.serializeFragment(insertedContent, {}, span);
            }

            labelSkillReferences(span);

            // Apply styling to all child elements to ensure visibility in nested structures (e.g., list items)
            const className = isHighlighted ? CLASSES.add : CLASSES.addDimmed;
            span.querySelectorAll("*").forEach((el) => {
              if (el instanceof HTMLElement) {
                el.className = el.className
                  ? `${el.className} ${className}`
                  : className;
              }
            });

            return span;
          },
          { side: -1 }
        )
      );
    }
  }

  return decorations;
}

function buildBlockAdditionWidget(
  pos: number,
  newChild: PMNode,
  isHighlighted: boolean,
  schema: Schema,
  suggestionId: string
): Decoration {
  const className = isHighlighted ? CLASSES.add : CLASSES.addDimmed;
  return Decoration.widget(
    pos,
    () => {
      const div = document.createElement("div");
      div.className = className;
      div.setAttribute(SUGGESTION_ID_ATTRIBUTE, suggestionId);
      div.contentEditable = "false";
      div.style.width = "fit-content";

      const serializer = DOMSerializer.fromSchema(schema);
      serializer.serializeFragment(Fragment.from(newChild), {}, div);

      labelSkillReferences(div);

      div.querySelectorAll("*").forEach((el) => {
        if (el instanceof HTMLElement) {
          el.className = el.className
            ? `${el.className} ${className}`
            : className;
        }
      });

      return div;
    },
    { side: -1 }
  );
}

// Create per-child-block decorations for root-level targets. Matches old and
// new children by position and diffs each pair for word-level inline diffs.
// Extra new blocks are shown as full additions, extra old blocks as deletions.
// When hiding unchanged blocks, a separator marks each hidden run that sits
// between two changes.
function buildRootDecorations({
  applyBlockHighlight,
  hideUnchangedBlocks,
  isHighlighted,
  newRoot,
  oldRoot,
  rootPos,
  schema,
  suggestionId,
}: {
  applyBlockHighlight: boolean;
  hideUnchangedBlocks: boolean;
  isHighlighted: boolean;
  newRoot: PMNode;
  oldRoot: PMNode;
  rootPos: number;
  schema: Schema;
  suggestionId: string;
}): Decoration[] {
  const decorations: Decoration[] = [];
  const oldChildren: PMNode[] = [];
  const newChildren: PMNode[] = [];
  oldRoot.content.forEach((child) => oldChildren.push(child));
  newRoot.content.forEach((child) => newChildren.push(child));

  // Apply a single decoration spanning the full root for a continuous highlight (no gaps between blocks).
  if (applyBlockHighlight) {
    decorations.push(
      Decoration.node(rootPos, rootPos + oldRoot.nodeSize, {
        class: CLASSES.blockHighlightDimmed,
        [SUGGESTION_ID_ATTRIBUTE]: suggestionId,
      })
    );
  }

  const maxLen = Math.max(oldChildren.length, newChildren.length);
  let oldOffset = 0;
  let hasSeenChange = false;
  let hiddenRunStart: number | null = null;

  for (let i = 0; i < maxLen; i++) {
    const oldChild = oldChildren[i];
    const newChild = newChildren[i];
    // Position of this old child block within the document.
    const childPos = rootPos + 1 + oldOffset;

    if (oldChild && newChild) {
      if (oldChild.content.size > 0) {
        const blockDecorations = buildBlockDecorations({
          applyBlockHighlight: false, // Root handles the highlight as a single decoration.
          blockPos: childPos,
          newNode: newChild,
          oldNode: oldChild,
          schema,
          suggestionId,
          isHighlighted,
        });
        decorations.push(...blockDecorations);
        if (blockDecorations.length === 0) {
          if (hideUnchangedBlocks) {
            decorations.push(
              Decoration.node(childPos, childPos + oldChild.nodeSize, {
                class: CLASSES.hidden,
              })
            );
            hiddenRunStart ??= childPos;
          }
          oldOffset += oldChild.nodeSize;
          continue;
        }
      } else {
        // Old block is empty (e.g. the default placeholder paragraph in a new editor).
        // Skip pairing — pairing would place the addition widget *after* the empty block,
        // causing a blank line before the first heading/paragraph in the card.
        decorations.push(
          buildBlockAdditionWidget(
            childPos,
            newChild,
            isHighlighted,
            schema,
            suggestionId
          )
        );
      }
      oldOffset += oldChild.nodeSize;
    } else if (oldChild) {
      // Block was removed: mark entire block content as deletion.
      if (oldChild.content.size > 0) {
        const contentStart = childPos + 1;
        decorations.push(
          Decoration.inline(
            contentStart,
            contentStart + oldChild.content.size,
            {
              class: isHighlighted ? CLASSES.remove : CLASSES.removeDimmed,
              [SUGGESTION_ID_ATTRIBUTE]: suggestionId,
            }
          )
        );
      }
      oldOffset += oldChild.nodeSize;
    } else if (newChild) {
      // Block was added: insert as a widget after the last old child.
      decorations.push(
        buildBlockAdditionWidget(
          childPos,
          newChild,
          isHighlighted,
          schema,
          suggestionId
        )
      );
    }

    if (hiddenRunStart !== null && hasSeenChange) {
      decorations.push(
        Decoration.widget(
          hiddenRunStart,
          () => {
            const separator = document.createElement("div");
            separator.className = CLASSES.unchangedSeparator;
            separator.contentEditable = "false";
            return separator;
          },
          { side: -1 }
        )
      );
    }
    hiddenRunStart = null;
    hasSeenChange = true;
  }

  return decorations;
}

interface SuggestionDisplayOptions {
  // Hides unchanged blocks of a root-level target, to show only its edits.
  hideUnchangedBlocks: boolean;
  showBlockHighlight: boolean;
}

function buildDecorations(
  state: EditorState,
  suggestions: Map<string, StoredSuggestion>,
  highlightedId: string | null,
  { hideUnchangedBlocks, showBlockHighlight }: SuggestionDisplayOptions
): DecorationSet {
  if (suggestions.size === 0) {
    return DecorationSet.empty;
  }

  const decorations: Decoration[] = [];
  const schema = state.schema;

  for (const [suggestionId, suggestion] of suggestions) {
    // Support prefix matching: a highlightedId of "abc" highlights both "abc" and "abc:0", "abc:1", etc.
    const isHighlighted =
      highlightedId !== null &&
      (suggestionId === highlightedId ||
        suggestionId.startsWith(highlightedId + ":"));
    const applyBlockHighlight = showBlockHighlight && isHighlighted;

    for (const op of suggestion.operations) {
      const found = findBlockByBlockId(state.doc, op.targetBlockId);
      if (!found) {
        continue;
      }

      const { node: blockNode, pos: blockPos } = found;

      const newNodes = parseHTMLToBlocks(op.newContent, op.targetBlockId, {
        document,
        domParser: PMDOMParser.fromSchema(schema),
      });
      if (newNodes.length === 0) {
        continue;
      }

      // For root-level targets, diff per-child block so that word-level diffs stay within their
      // block and block boundaries are preserved. For single-block targets, diff the first new
      // block against the old block and show any extra new blocks as addition widgets.
      if (
        blockNode.type.name === INSTRUCTIONS_ROOT_NODE_NAME &&
        newNodes[0].type.name === INSTRUCTIONS_ROOT_NODE_NAME
      ) {
        decorations.push(
          ...buildRootDecorations({
            applyBlockHighlight,
            hideUnchangedBlocks,
            oldRoot: blockNode,
            newRoot: newNodes[0],
            rootPos: blockPos,
            schema,
            suggestionId,
            isHighlighted,
          })
        );
      } else {
        decorations.push(
          ...buildBlockDecorations({
            applyBlockHighlight,
            oldNode: blockNode,
            newNode: newNodes[0],
            blockPos,
            schema,
            suggestionId,
            isHighlighted,
          })
        );

        // Extra new blocks shown as full addition widgets after the old block.
        const afterBlockPos = blockPos + blockNode.nodeSize;
        for (let i = 1; i < newNodes.length; i++) {
          decorations.push(
            buildBlockAdditionWidget(
              afterBlockPos,
              newNodes[i],
              isHighlighted,
              schema,
              suggestionId
            )
          );
        }
      }
    }
  }

  return DecorationSet.create(state.doc, decorations);
}

// Check if a transaction step modifies a specific range
function stepModifiesRange(
  step: { forEach: (f: (oldStart: number, oldEnd: number) => void) => void },
  rangeStart: number,
  rangeEnd: number
): boolean {
  let modifies = false;
  step.forEach((oldStart, oldEnd) => {
    // Ranges overlap if they don't end before or start after each other
    if (oldEnd > rangeStart && oldStart < rangeEnd) {
      modifies = true;
    }
  });
  return modifies;
}

function createPlugin(
  getHighlightedId: () => string | null,
  displayOptions: SuggestionDisplayOptions
) {
  return new Plugin<PluginState>({
    key: pluginKey,

    state: {
      init(): PluginState {
        return {
          decorations: DecorationSet.empty,
          highlightedId: null,
          suggestions: new Map(),
        };
      },

      apply(tr, value, _oldState, newState): PluginState {
        const meta = tr.getMeta(pluginKey);
        let { suggestions, highlightedId } = value;
        let dirty = false;

        if (meta?.type === "add") {
          suggestions = new Map(suggestions);
          suggestions.set(meta.suggestion.id, meta.suggestion);
          dirty = true;
        }

        if (meta?.type === "remove") {
          suggestions = new Map(suggestions);
          suggestions.delete(meta.id);
          dirty = true;
        }

        if (meta?.type === "removeAll") {
          suggestions = new Map(suggestions);
          for (const id of meta.ids) {
            suggestions.delete(id);
          }
          dirty = true;
        }

        if (meta?.type === "highlight") {
          highlightedId = meta.id;
          dirty = true;
        }

        if (dirty || tr.docChanged) {
          const effectiveHighlightedId = highlightedId ?? getHighlightedId();
          return {
            suggestions,
            highlightedId,
            decorations: buildDecorations(
              newState,
              suggestions,
              effectiveHighlightedId,
              displayOptions
            ),
          };
        }

        return value;
      },
    },

    props: {
      decorations(editorState) {
        return (
          pluginKey.getState(editorState)?.decorations ?? DecorationSet.empty
        );
      },
    },

    filterTransaction(tr, state) {
      if (!tr.docChanged || tr.getMeta(pluginKey)) {
        return true;
      }

      const pluginState = pluginKey.getState(state);
      if (!pluginState || pluginState.suggestions.size === 0) {
        return true;
      }

      // Collect all suggestion block content ranges upfront to avoid redundant lookups.
      // Typical case: 1-5 suggestions with 1-2 operations each = ~10 ranges max.
      // Even with 20 suggestions × 5 operations × 5 steps = 500 iterations, this is negligible.
      const suggestionRanges: Array<{ start: number; end: number }> = [];
      for (const suggestion of pluginState.suggestions.values()) {
        for (const op of suggestion.operations) {
          const found = findBlockByBlockId(state.doc, op.targetBlockId);
          if (found) {
            suggestionRanges.push({
              start: found.pos + 1,
              end: found.pos + found.node.nodeSize - 1,
            });
          }
        }
      }

      // Check each transaction step against all suggestion ranges.
      // This is O(steps × ranges) but typically O(1-5 × 1-10) = ~50 operations max.
      for (let i = 0; i < tr.steps.length; i++) {
        const map = tr.mapping.slice(0, i);

        for (const range of suggestionRanges) {
          const mappedStart = map.map(range.start);
          const mappedEnd = map.map(range.end);

          if (stepModifiesRange(tr.mapping.maps[i], mappedStart, mappedEnd)) {
            return false;
          }
        }
      }

      return true;
    },
  });
}

interface ApplySuggestionOptions {
  id: string;
  targetBlockId: string;
  // HTML content for the block (e.g., '<p>New text with <strong>bold</strong></p>').
  content: string;
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    instructionSuggestion: {
      acceptAllSuggestions: () => ReturnType;
      acceptSuggestion: (suggestionId: string) => ReturnType;
      applySuggestion: (options: ApplySuggestionOptions) => ReturnType;
      rejectAllSuggestions: () => ReturnType;
      rejectSuggestion: (suggestionId: string) => ReturnType;
      setHighlightedSuggestion: (suggestionId: string | null) => ReturnType;
    };
  }

  interface Storage {
    instructionSuggestion: {
      highlightedSuggestionId: string | null;
    };
  }
}

export function getActiveSuggestions(
  state: EditorState
): Map<string, StoredSuggestion> {
  return pluginKey.getState(state)?.suggestions ?? new Map();
}

export function getActiveSuggestionIds(state: EditorState): string[] {
  return Array.from(getActiveSuggestions(state).keys());
}

export function getSuggestionPosition(
  editor: { state: EditorState },
  suggestionId: string
): number | null {
  const state = pluginKey.getState(editor.state);
  if (!state) {
    return null;
  }

  const suggestion = state.suggestions.get(suggestionId);
  if (!suggestion || suggestion.operations.length === 0) {
    return null;
  }

  const found = findBlockByBlockId(
    editor.state.doc,
    suggestion.operations[0].targetBlockId
  );
  return found ? found.pos + 1 : null;
}

export const InstructionSuggestionExtension = Extension.create<{
  hideUnchangedBlocks: boolean;
  showBlockHighlight: boolean;
}>({
  name: "instructionSuggestion",

  addOptions() {
    return {
      hideUnchangedBlocks: false,
      showBlockHighlight: true,
    };
  },

  addStorage() {
    return {
      highlightedSuggestionId: null as string | null,
    };
  },

  addProseMirrorPlugins() {
    return [
      createPlugin(() => this.storage.highlightedSuggestionId, this.options),
    ];
  },

  addCommands() {
    return {
      applySuggestion:
        (options: ApplySuggestionOptions) =>
        ({ tr, state, dispatch }) => {
          const { id, targetBlockId, content } = options;

          if (!findBlockByBlockId(state.doc, targetBlockId)) {
            return false;
          }

          if (dispatch) {
            const suggestion: StoredSuggestion = {
              id,
              operations: [{ targetBlockId, newContent: content }],
            };
            tr.setMeta(pluginKey, { type: "add", suggestion });
            dispatch(tr);
          }

          return true;
        },

      acceptSuggestion:
        (suggestionId: string) =>
        ({ state, tr, dispatch }) => {
          const pluginState = pluginKey.getState(state);
          const suggestion = pluginState?.suggestions.get(suggestionId);
          if (!suggestion) {
            return false;
          }

          if (dispatch) {
            const schema = state.schema;

            for (const op of suggestion.operations) {
              const found = findBlockByBlockId(tr.doc, op.targetBlockId);
              if (!found) {
                continue;
              }

              const newNodes = parseHTMLToBlocks(
                op.newContent,
                op.targetBlockId,
                { document, domParser: PMDOMParser.fromSchema(schema) }
              );
              if (newNodes.length === 0) {
                continue;
              }

              replaceBlock(tr, found, newNodes);
            }

            tr.setMeta(pluginKey, { type: "remove", id: suggestionId });
            dispatch(tr);
          }

          return true;
        },

      rejectSuggestion:
        (suggestionId: string) =>
        ({ state, tr, dispatch }) => {
          if (!pluginKey.getState(state)?.suggestions.has(suggestionId)) {
            return false;
          }

          if (dispatch) {
            tr.setMeta(pluginKey, { type: "remove", id: suggestionId });
            dispatch(tr);
          }

          return true;
        },

      acceptAllSuggestions:
        () =>
        ({ commands, state, tr }) => {
          const ids = getActiveSuggestionIds(state);
          if (ids.length === 0) {
            return false;
          }

          // commands.acceptSuggestion shares the same tr, so content
          // replacements accumulate correctly. However each call overwrites
          // the plugin meta, so only the last suggestion would be removed
          // from plugin state. We fix this by overwriting the meta with all
          // IDs after the loop.
          const allSucceeded = ids
            .map((id) => commands.acceptSuggestion(id))
            .every(Boolean);
          tr.setMeta(pluginKey, { type: "removeAll", ids });

          return allSucceeded;
        },

      rejectAllSuggestions:
        () =>
        ({ state, tr, dispatch }) => {
          const ids = getActiveSuggestionIds(state);
          if (ids.length === 0) {
            return false;
          }

          if (dispatch) {
            tr.setMeta(pluginKey, { type: "removeAll", ids });
            dispatch(tr);
          }

          return true;
        },

      setHighlightedSuggestion:
        (suggestionId: string | null) =>
        ({ tr, dispatch }) => {
          this.storage.highlightedSuggestionId = suggestionId;

          if (dispatch) {
            tr.setMeta(pluginKey, { type: "highlight", id: suggestionId });
            dispatch(tr);
          }

          return true;
        },
    };
  },
});
